import { ReactNode, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { ToastContainer } from 'react-toastify';
import styled, { css } from 'styled-components/macro';
import { useMenuContext } from '../../hooks/context/useMenuContext';
import { StyledGlobals } from '../../styles/StyledGlobals';
import { breakpoint, max_width } from '../../styles/constants';
import { SLUG_CART, SLUG_DELIVERY, SLUG_RECAP } from '../../styles/slugs';
import { MainMenu } from './menu/desktop/MainMenu';
import { MobileMenu } from './menu/mobile/MobileMenu';
import { Nav } from './nav/Nav';

export type MenuItem = {
  id: number;
  parentId: number;
  name: string;
  description: string;
  slug: string;
  children: MenuItem[];
};

export type MenuProps = { menu: MenuItem[] };

export type ChildrenProps = {
  children: ReactNode;
};

export const Layout = () => {
  const { open, selected } = useMenuContext();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const scrollbarWidth =
      typeof window !== `undefined`
        ? window.innerWidth - document.body.clientWidth
        : 0;
    document.body.style.setProperty(
      '--viewportWidth',
      `calc(100vw - ${scrollbarWidth}px)`,
    );
  }, [location.pathname]);

  let noTransform = false;
  if (
    location.pathname.includes(SLUG_CART) ||
    location.pathname.includes(SLUG_RECAP) ||
    location.pathname.includes(SLUG_DELIVERY)
  )
    noTransform = true;

  return (
    <>
      <StyledGlobals>
        <Container>
          <Screen open={open} noTransform={noTransform}>
            <MainMenu />
            <MainContainer>
              <Main>
                <Nav />
                <Opacity openSub={selected !== -1}>
                  <Outlet />
                </Opacity>
              </Main>
            </MainContainer>
          </Screen>
          <MobileMenu />
        </Container>
        <StyledToastContainer
          hideProgressBar
          autoClose={3000}
          position={'top-right'}
        />
      </StyledGlobals>
    </>
  );
};

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.black};
  position: relative;
  min-height: 101vh;
`;

const Screen = styled.div<{ open: boolean; noTransform: boolean }>`
  ${({ noTransform, open }) =>
    noTransform
      ? css`
          transform: none;
        `
      : css`
          transform: ${open ? 'translateX(91%)' : 'translateX(0)'};
        `}

  transition: transform ease-out 250ms;
  background-color: inherit;
  margin-inline: 1.25rem;
  min-width: 17.5rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  @media (min-width: ${breakpoint}) {
    transform: none;
    max-height: unset;
    max-width: ${max_width};
    margin-inline: auto;
  }
`;

const Main = styled.main`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MainContainer = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const Opacity = styled.div<{ openSub: boolean }>`
  opacity: ${({ openSub }) => (openSub ? '0.8' : '1')};
`;

const StyledToastContainer = styled(ToastContainer)`
  .Toastify__toast-theme--light {
    background-color: transparent;
  }

  .Toastify__toast-container {
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .Toastify__toast {
    box-shadow: none;
    padding: unset;
    margin-bottom: unset;
  }

  .Toastify__close-button {
    display: none;
  }

  .Toastify__toast-body {
    padding: 0 20px;
  }
`;
